import { useState, useEffect, useRef, useCallback } from "react";
import { DJAudioEngine } from "../services/DJAudioEngine";

export const useDJAudioSystem = () => {
  const engineRef = useRef(null);
  const [isSupported, setIsSupported] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Deck states sync with React
  const [deckStates, setDeckStates] = useState({});
  
  // Mixer / Master states
  const [masterVolume, setMasterVolumeState] = useState(0.8);
  const [crossfader, setCrossfaderState] = useState(0);

  // Microphone states
  const [micState, setMicState] = useState({
    isActive: false,
    volume: 0.5,
    selectedDeviceId: "",
    autoDucking: false,
    voiceFxType: "clean",
    inputMode: "raw",        // "raw" = no browser voice DSP (headphones); "speaker" = echo-cancelled
    feedbackGuardActive: false,
    health: "idle",          // idle | live | recovering | lost
    healthDetail: null,
    echoDelayTime: 650, // 650ms default delay time
    echoRepeats: 3,     // 3 repeats default ("Dj Kahnu, Dj Kahnu, Dj Kahnu")
    eqLow: 0,
    eqMid: 0,
    eqHigh: 0
  });
  const [availableMics, setAvailableMics] = useState([]);
  const [availableOutputs, setAvailableOutputs] = useState([]);
  const [selectedOutputId, setSelectedOutputId] = useState("");
  const [duckingFactor, setDuckingFactorState] = useState(0.3);

  // Recording states
  const [recordingState, setRecordingState] = useState({
    isRecording: false,
    duration: 0,
    resultUrl: null,
    resultBlob: null,
    format: null,        // "mp3" normally; "wav"/"webm" only if MP3 encoding was unavailable
    isEncoding: false,
    encodeProgress: 0
  });

  // Master CPU usage / Audio Diagnostic mocks for DJ visual feedback
  const [diagnostics, setDiagnostics] = useState({
    cpuUsage: 0,
    latency: 5,
    sampleRate: 44100
  });

  // Update deck state dictionary for React render updates
  const updateDecksList = useCallback(() => {
    if (!engineRef.current) return;
    const states = {};
    engineRef.current.decks.forEach((deck, id) => {
      states[id] = {
        id: deck.id,
        name: deck.name,
        thumbnail: deck.thumbnail,
        channel: deck.channel,
        duration: deck.duration,
        currentTime: engineRef.current.getDeckCurrentTime(id),
        isPlaying: deck.isPlaying,
        playbackRate: deck.playbackRate,
        pitchPercent: deck.pitchPercent,
        bpm: deck.bpm,
        originalBpm: deck.originalBpm,
        volume: deck.volume,
        pan: deck.pan,
        loop: deck.loop,
        loopStart: deck.loopStart,
        loopEnd: deck.loopEnd,
        cues: deck.cues,
        crossfadeSide: deck.crossfadeSide,
        stems: deck.stems ? { ...deck.stems } : { vocal: 0, instrumental: 0, beats: 0, mode: "stems" },
        fxConfig: { ...deck.fxConfig },
        audioBuffer: deck.audioBuffer
      };
    });
    setDeckStates(states);
  }, []);

  // Check Web Audio API support and auto-initialize engine + devices on mount
  useEffect(() => {
    const supported = !!(window.AudioContext || window.webkitAudioContext);
    setIsSupported(supported);
    
    if (supported) {
      const autoInit = async () => {
        if (!engineRef.current) {
          engineRef.current = new DJAudioEngine();
          engineRef.current.initialize();
          window.djEngine = engineRef.current;
          
          // Synchronize automatic deck stops back to React
          engineRef.current.onDeckEnded = (deckId) => {
            updateDecksList();
          };
          
          // Create Deck A and Deck B by default
          engineRef.current.createDeck("A", "left");
          engineRef.current.createDeck("B", "right");

          setInitialized(true);
          updateDecksList();

          // Query hardware devices instantly on page load.
          // This probe stream exists only to unlock device labels — release it immediately.
          // Chrome shares one audio-processing module per capture device, so a lingering
          // probe stream (which defaults to echoCancellation: true) would force echo
          // cancellation onto the real mic stream opened later and chop up speech.
          try {
            const probe = await navigator.mediaDevices
              .getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } })
              .catch(() => null);
            if (probe) probe.getTracks().forEach(t => t.stop());
            const devices = await navigator.mediaDevices.enumerateDevices();
            
            // Microphone inputs
            const mics = devices.filter(d => d.kind === "audioinput");
            setAvailableMics(mics);
            if (mics.length > 0) {
              setMicState(prev => ({ ...prev, selectedDeviceId: mics[0].deviceId }));
            }

            // Speaker / Headphone outputs
            const outputs = devices.filter(d => d.kind === "audiooutput");
            setAvailableOutputs(outputs);
          } catch (e) {
            console.warn("Could not list audio devices on mount", e);
          }
        }
      };
      autoInit();
    }
  }, [updateDecksList]);

  // Keep the device pickers in sync with the hardware. Plugging or unplugging a headset
  // on macOS rewrites the device list and invalidates deviceIds, so a stale list means
  // the mic picker points at a device that no longer exists.
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.addEventListener) return;

    const refreshDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const mics = devices.filter(d => d.kind === "audioinput");
        const outputs = devices.filter(d => d.kind === "audiooutput");
        setAvailableMics(mics);
        setAvailableOutputs(outputs);

        // If the selected input vanished, fall back to the system default
        setMicState(prev => {
          if (!prev.selectedDeviceId) return prev;
          const stillThere = mics.some(m => m.deviceId === prev.selectedDeviceId);
          return stillThere ? prev : { ...prev, selectedDeviceId: "" };
        });
      } catch (e) {
        console.warn("Could not refresh audio devices", e);
      }
    };

    navigator.mediaDevices.addEventListener("devicechange", refreshDevices);
    return () => navigator.mediaDevices.removeEventListener("devicechange", refreshDevices);
  }, []);

  // Keep initEngine fallback
  const initEngine = useCallback(async () => {
    // Handled on mount automatically
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (engineRef.current) {
        engineRef.current.destroy();
        engineRef.current = null;
        window.djEngine = null;
      }
    };
  }, []);



  // Playhead and throttled visual metrics loop
  const animationFrameId = useRef(null);

  useEffect(() => {
    if (!initialized) return;

    let lastDiagnosticsTime = 0;
    let lastRecordingTime = 0;

    const updateMetrics = () => {
      if (!engineRef.current) {
        animationFrameId.current = requestAnimationFrame(updateMetrics);
        return;
      }

      const engine = engineRef.current;
      const ctx = engine.audioContext;

      if (ctx) {
        // Microphone level indicator direct DOM updates (0ms React re-render lag)
        if (engine.isMicActive && engine.micAnalyserNode) {
          const dataArray = new Uint8Array(engine.micAnalyserNode.fftSize);
          engine.micAnalyserNode.getByteTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const val = (dataArray[i] - 128) / 128;
            sum += val * val;
          }
          const rms = Math.sqrt(sum / dataArray.length);
          const micLvl = Math.min(100, Math.round(rms * 400));
          const el = document.getElementById("mic-level-bar");
          if (el) {
            el.style.width = `${micLvl}%`;
          }
        } else {
          const el = document.getElementById("mic-level-bar");
          if (el) {
            el.style.width = "0%";
          }
        }

        // Sync Decks current playback times dynamically (avoid full React re-render lag)
        engine.decks.forEach((deck, id) => {
          const time = engine.getDeckCurrentTime(id);
          const el = document.getElementById(`playhead-${id}`);
          if (el) {
            el.innerText = formatTime(time);
          }
          const barEl = document.getElementById(`progress-bar-${id}`);
          if (barEl && deck.duration > 0) {
            barEl.style.width = `${(time / deck.duration) * 100}%`;
          }
        });

        const nowStamp = performance.now();

        // Throttled diagnostics calculations (every 2 seconds) to prevent CPU spikes and render flickers
        if (nowStamp - lastDiagnosticsTime > 2000) {
          let activeCount = 0;
          engine.decks.forEach(d => {
            if (d.isPlaying) activeCount++;
          });
          
          // Realistic, stable CPU load with smooth wave jitter (rather than drastic white noise random spikes)
          const baseCpu = 3 + (activeCount * 3.5) + (engine.isRecording ? 2.5 : 0);
          const jitter = Math.sin(nowStamp / 1500) * 1.5;
          const finalCpu = Math.max(1, Math.min(99, Math.round(baseCpu + jitter)));

          setDiagnostics({
            cpuUsage: finalCpu,
            latency: Math.round(ctx.baseLatency ? ctx.baseLatency * 1000 : 4),
            sampleRate: ctx.sampleRate
          });
          lastDiagnosticsTime = nowStamp;
        }

        // Throttled recording duration sync (every 1 second)
        if (engine.isRecording && nowStamp - lastRecordingTime > 1000) {
          setRecordingState(prev => ({
            ...prev,
            isRecording: true,
            duration: Math.round(engine.recordingElapsedTime)
          }));
          lastRecordingTime = nowStamp;
        }
      }

      animationFrameId.current = requestAnimationFrame(updateMetrics);
    };

    updateMetrics();

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [initialized]);

  // Format MM:SS
  const formatTime = (secs) => {
    if (isNaN(secs)) return "00:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Create dynamic extra decks (C, D, etc.)
  const addDeck = useCallback((id, crossfadeSide = "center") => {
    if (!engineRef.current) return;
    engineRef.current.createDeck(id, crossfadeSide);
    updateDecksList();
  }, [updateDecksList]);

  const removeDeck = useCallback((id) => {
    if (!engineRef.current) return;
    engineRef.current.destroyDeck(id);
    updateDecksList();
  }, [updateDecksList]);

  // Deck controls wrapper
  const loadTrack = useCallback(async (id, fileOrBlob, metadata = {}) => {
    if (!engineRef.current) return;
    await engineRef.current.loadTrack(id, fileOrBlob, metadata);
    updateDecksList();
  }, [updateDecksList]);

  const loadTrackFromUrl = useCallback(async (id, url, metadata = {}) => {
    if (!engineRef.current) return;
    await engineRef.current.loadTrackFromUrl(id, url, metadata);
    updateDecksList();
  }, [updateDecksList]);

  const playDeck = useCallback((id) => {
    if (!engineRef.current) return;
    engineRef.current.playDeck(id);
    updateDecksList();
  }, [updateDecksList]);

  const pauseDeck = useCallback((id) => {
    if (!engineRef.current) return;
    engineRef.current.pauseDeck(id);
    updateDecksList();
  }, [updateDecksList]);

  const seekDeck = useCallback((id, seconds) => {
    if (!engineRef.current) return;
    engineRef.current.seekDeck(id, seconds);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckVolume = useCallback((id, vol) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckVolume(id, vol);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckPan = useCallback((id, pan) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckPan(id, pan);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckPitch = useCallback((id, pitchPercent) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckPitch(id, pitchPercent);
    updateDecksList();
  }, [updateDecksList]);

  const nudgeDeckPitch = useCallback((id, direction) => {
    if (!engineRef.current) return;
    const deck = engineRef.current.decks.get(id);
    if (!deck) return;

    // Apply audio platter pitch bend nudge
    engineRef.current.nudgeDeckPitch(id, direction);

    // Micro-adjust pitch percentage permanently by 0.1% on each click
    const step = 0.1;
    const newPitch = Math.max(-10.0, Math.min(10.0, deck.pitchPercent + (direction > 0 ? step : -step)));
    engineRef.current.setDeckPitch(id, newPitch);

    updateDecksList();
  }, [updateDecksList]);

  const syncDeckBpm = useCallback((id, targetBpm) => {
    if (!engineRef.current) return;
    engineRef.current.syncDeckBpm(id, targetBpm);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckLoop = useCallback((id, loopActive, start = 0, end = null) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckLoop(id, loopActive, start, end);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckCrossfadeSide = useCallback((id, side) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckCrossfadeSide(id, side);
    updateDecksList();
  }, [updateDecksList]);

  const triggerCue = useCallback((id) => {
    if (!engineRef.current) return;
    engineRef.current.triggerCue(id);
    updateDecksList();
  }, [updateDecksList]);

  const setCuePoint = useCallback((id, index = 0) => {
    if (!engineRef.current) return;
    engineRef.current.setCuePoint(id, index);
    updateDecksList();
  }, [updateDecksList]);

  const clearCuePoint = useCallback((id, index = 0) => {
    if (!engineRef.current) return;
    engineRef.current.clearCuePoint(id, index);
    updateDecksList();
  }, [updateDecksList]);

  // EQs & FXs
  const setDeckEqLow = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckEqLow(id, val);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckEqMid = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckEqMid(id, val);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckEqHigh = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckEqHigh(id, val);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckFilter = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckFilter(id, val);
    updateDecksList();
  }, [updateDecksList]);

  // STEMS Controls (Vocal, Instrumental, Beats)
  const setDeckStemVocal = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckStemVocal(id, val);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckStemInstrumental = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckStemInstrumental(id, val);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckStemBeats = useCallback((id, val) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckStemBeats(id, val);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckStemMode = useCallback((id, mode) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckStemMode(id, mode);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckDelay = useCallback((id, mix, time, feedback) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckDelay(id, mix, time, feedback);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckReverb = useCallback((id, mix) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckReverb(id, mix);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckDistortion = useCallback((id, amount) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckDistortion(id, amount);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckSolo = useCallback((id, active) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckSolo(id, active);
    updateDecksList();
  }, [updateDecksList]);

  const setDeckMute = useCallback((id, active) => {
    if (!engineRef.current) return;
    engineRef.current.setDeckMute(id, active);
    updateDecksList();
  }, [updateDecksList]);

  const scratchDeck = useCallback((id, speedFactor) => {
    if (!engineRef.current) return;
    const deck = engineRef.current.decks.get(id);
    if (deck && !deck.isPlaying && !deck.isScratching) {
      deck.wasPaused = true;
      deck.isScratching = true;
      engineRef.current.playDeck(id);
    }
    engineRef.current.scratchDeck(id, speedFactor);
  }, []);

  const releaseScratch = useCallback((id) => {
    if (!engineRef.current) return;
    const deck = engineRef.current.decks.get(id);
    engineRef.current.releaseScratch(id);
    if (deck) {
      deck.isScratching = false;
      if (deck.wasPaused) {
        engineRef.current.pauseDeck(id);
        deck.wasPaused = false;
      }
    }
    updateDecksList();
  }, [updateDecksList]);

  // Mixer / Master
  const setCrossfader = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setCrossfader(val);
    setCrossfaderState(val);
  }, []);

  const setMasterVolumeVal = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setMasterVolume(val);
    setMasterVolumeState(val);
  }, []);

  // Microphone
  const startMicrophone = useCallback(async (deviceId = null, inputMode = null) => {
    if (!engineRef.current) return;
    engineRef.current.onMicFeedbackGuard = (isDucking) => {
      setMicState(prev => ({ ...prev, feedbackGuardActive: isDucking }));
    };
    engineRef.current.onMicHealthChange = (health, detail) => {
      setMicState(prev => ({
        ...prev,
        health,
        healthDetail: detail,
        // Never leave the console reading LIVE when the engine has no stream
        isActive: health === "lost" ? false : prev.isActive
      }));
    };
    const success = await engineRef.current.startMicrophone(deviceId, inputMode);
    setMicState(prev => ({
      ...prev,
      isActive: success,
      inputMode: engineRef.current.micInputMode,
      selectedDeviceId: deviceId || prev.selectedDeviceId
    }));
  }, []);

  const setMicInputMode = useCallback(async (mode) => {
    if (!engineRef.current) return;
    setMicState(prev => ({ ...prev, inputMode: mode }));
    await engineRef.current.setMicInputMode(mode, micState.selectedDeviceId || null);
  }, [micState.selectedDeviceId]);

  const stopMicrophone = useCallback(() => {
    if (!engineRef.current) return;
    engineRef.current.stopMicrophone();
    setMicState(prev => ({ ...prev, isActive: false }));
  }, []);

  const selectMicDevice = useCallback((deviceId) => {
    setMicState(prev => ({ ...prev, selectedDeviceId: deviceId }));
  }, []);

  const setAudioOutputDevice = useCallback(async (sinkId) => {
    if (!engineRef.current) return;
    const success = await engineRef.current.setAudioOutputDevice(sinkId);
    if (success) {
      setSelectedOutputId(sinkId);
    }
  }, []);

  const setDuckingFactor = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setDuckingFactor(val);
    setDuckingFactorState(val);
  }, []);

  const setMicVolume = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setMicVolume(val);
    setMicState(prev => ({ ...prev, volume: val }));
  }, []);

  const setMicEqLowVal = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setMicEqLow(val);
    setMicState(prev => ({ ...prev, eqLow: val }));
  }, []);

  const setMicEqMidVal = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setMicEqMid(val);
    setMicState(prev => ({ ...prev, eqMid: val }));
  }, []);

  const setMicEqHighVal = useCallback((val) => {
    if (!engineRef.current) return;
    engineRef.current.setMicEqHigh(val);
    setMicState(prev => ({ ...prev, eqHigh: val }));
  }, []);

  const setMicEchoDelay = useCallback((ms) => {
    setMicState(prev => ({ ...prev, echoDelayTime: ms }));
    if (engineRef.current) {
      engineRef.current.setMicEchoDelayTime(ms);
    }
  }, []);

  const setMicEchoRepeats = useCallback((count) => {
    setMicState(prev => ({ ...prev, echoRepeats: count }));
    if (engineRef.current) {
      engineRef.current.setMicEchoRepeats(count);
    }
  }, []);

  const applyVoiceFx = useCallback((type) => {
    setMicState(prev => ({ ...prev, voiceFxType: type }));
    if (engineRef.current) {
      engineRef.current.applyVoiceFx(type);
    }
  }, []);

  const setAutoDucking = useCallback((enabled) => {
    if (!engineRef.current) return;
    engineRef.current.setAutoDucking(enabled);
    setMicState(prev => ({ ...prev, autoDucking: enabled }));
  }, []);

  // Sampler
  const playSample = useCallback((index) => {
    if (!engineRef.current) return;
    engineRef.current.playSample(index);
  }, []);

  // Recording
  const startRecording = useCallback(() => {
    if (!engineRef.current) return;
    const success = engineRef.current.startRecording();
    if (success) {
      setRecordingState({
        isRecording: true,
        duration: 0,
        resultUrl: null,
        resultBlob: null,
        format: null,
        isEncoding: false,
        encodeProgress: 0
      });
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (!engineRef.current) return;

    // MP3 encoding of a long mix takes real time — surface it instead of appearing hung
    engineRef.current.onRecordingEncodeProgress = (pct) => {
      setRecordingState(prev => ({ ...prev, isEncoding: pct < 100, encodeProgress: pct }));
    };
    setRecordingState(prev => ({ ...prev, isRecording: false, isEncoding: true, encodeProgress: 0 }));

    const result = await engineRef.current.stopRecording();
    if (result) {
      setRecordingState({
        isRecording: false,
        duration: result.duration,
        resultUrl: result.url,
        resultBlob: result.blob,
        format: result.format || "mp3",
        isEncoding: false,
        encodeProgress: 100
      });
    } else {
      setRecordingState(prev => ({ ...prev, isEncoding: false }));
    }
  }, []);

  const clearRecordingResult = useCallback(() => {
    setRecordingState(prev => {
      // Release the object URL so long sessions do not leak blobs
      if (prev.resultUrl) URL.revokeObjectURL(prev.resultUrl);
      return {
        isRecording: false,
        duration: 0,
        resultUrl: null,
        resultBlob: null,
        format: null,
        isEncoding: false,
        encodeProgress: 0
      };
    });
  }, []);

  // Exposure of Audio Nodes for Canvas-Visualizer drawing
  const getDeckAnalyser = (id) => {
    const deck = engineRef.current?.decks?.get(id);
    return deck ? deck.nodes.analyser : null;
  };

  const getMasterAnalyser = () => {
    return engineRef.current?.masterAnalyserNode || null;
  };

  return {
    isSupported,
    initialized,
    initEngine,
    deckStates,
    masterVolume,
    crossfader,
    micState,
    availableMics,
    availableOutputs,
    selectedOutputId,
    duckingFactor,
    recordingState,
    diagnostics,
    
    // Actions
    addDeck,
    removeDeck,
    loadTrack,
    loadTrackFromUrl,
    playDeck,
    pauseDeck,
    seekDeck,
    setDeckVolume,
    setDeckPan,
    setDeckPitch,
    nudgeDeckPitch,
    syncDeckBpm,
    setDeckLoop,
    setDeckCrossfadeSide,
    triggerCue,
    setCuePoint,
    clearCuePoint,
    setDeckEqLow,
    setDeckEqMid,
    setDeckEqHigh,
    setDeckFilter,
    setDeckStemVocal,
    setDeckStemInstrumental,
    setDeckStemBeats,
    setDeckStemMode,
    setDeckDelay,
    setDeckReverb,
    setDeckDistortion,
    setDeckSolo,
    setDeckMute,
    scratchDeck,
    releaseScratch,
    
    // Master
    setCrossfader,
    setMasterVolume: setMasterVolumeVal,
    setAudioOutputDevice,
    
    // Mic
    startMicrophone,
    stopMicrophone,
    selectMicDevice,
    setMicInputMode,
    setMicVolume,
    setMicEqLow: setMicEqLowVal,
    setMicEqMid: setMicEqMidVal,
    setMicEqHigh: setMicEqHighVal,
    setMicEchoDelay,
    setMicEchoRepeats,
    applyVoiceFx,
    setAutoDucking,
    setDuckingFactor,
    
    // Sampler
    playSample,
    
    // Record
    startRecording,
    stopRecording,
    clearRecordingResult,
    
    // Node references for rendering
    getDeckAnalyser,
    getMasterAnalyser
  };
};
